/* eslint prefer-spread: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import {
  Grid,
  Table,
  Progress,
  Segment,
  Loader,
  Dimmer,
} from 'semantic-ui-react';
import CountUp from 'react-countup';
import qs from 'qs';

import { decodeSearch } from '../../utils/url';
import { calcConversionAmount, getCompanyBaseCurrency, getDisplayCurrency, getDisplayCurrencySymbol } from '../../utils/currencies';
import config from '../../../config';
import { fetchM } from '../../actions/fetchM';
import { flashErrorMessage } from '../../actions/flash';
import LoanAmount from '../../components/LoanAmount';
import BOOKING_STATUSES from '../../constants/bookingStatuses';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';
import CurrencySymbol from '../../components/CurrencySymbol';
import OverallBookingReports from './OverallBookingReports';

class BookingsByStatus extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    appConfig: PropTypes.shape({
      data: PropTypes.shape({
        currency: PropTypes.shape({
          code: PropTypes.string,
          symbol: PropTypes.string,
        }),
      }),
    }),
    auth: PropTypes.shape({
      company: PropTypes.object,
      isHeadquarters: PropTypes.bool,
      isSuperAdmin: PropTypes.bool,
    }),
    barChart: PropTypes.bool,
    headerColor: PropTypes.string,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      query: PropTypes.object,
      search: PropTypes.string,
    }).isRequired,
    projectID: PropTypes.string,
    projectCurrencies: PropTypes.oneOfType([
      PropTypes.array,
    ]),
    t: PropTypes.func,
  }
  static mapStateToProps = state => ({
    appConfig: state.appConfig,
    auth: state.auth,
  });
  static getQueryData = (props) => {
    const query = decodeSearch(props.location.search);
    const projectID = query.projectID || '';
    const dateRange = query.dateRange || '';
    const dateFrom = query.dateFrom ? query.dateFrom : '';
    const dateTo = query.dateTo ? query.dateTo : '';
    const businessUnitID = query.businessUnitID ? query.businessUnitID : '';
    return {
      projectID,
      dateRange,
      dateFrom,
      dateTo,
      businessUnitID,
    };
  }
  constructor(props) {
    super(props);
    const projectCurrencies = props.projectCurrencies;
    this.state = {
      reportData: [],
      loading: true,
      projectCurrencies,
    };
  }
  componentDidMount() {
    this.fetchReports();
  }
  componentWillReceiveProps(nextProps) {
    const query = BookingsByStatus.getQueryData(this.props);
    const newQuery = BookingsByStatus.getQueryData(nextProps);
    const projectCurrencies = nextProps.projectCurrencies;
    this.setState({
      projectCurrencies,
    });
    if (
      (query.dateRange !== newQuery.dateRange) ||
      (query.dateTo !== newQuery.dateTo) ||
      (query.dateFrom !== newQuery.dateFrom) ||
      (query.projectID !== newQuery.projectID) ||
      (query.businessUnitID !== newQuery.businessUnitID)
    ) {
      this.setState({
        reportData: [],
      }, () => {
        this.fetchReports(nextProps);
      });
    }
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  getConversionAmount(amount) {
    const { projectCurrencies = [] } = this.state;
    const { company } = this.props.auth;
    const companyBaseCurrency = getCompanyBaseCurrency(company);
    const displayCurrency = getDisplayCurrency();
    const companyCurrencyInProject = projectCurrencies.find(e => e.code === companyBaseCurrency.code);
    const displayCurrencyInProject = projectCurrencies.find(e => e.code === displayCurrency);
    const projectBaseCurrency = projectCurrencies.find(e => e.isDefault);
    return calcConversionAmount(
      companyCurrencyInProject,
      displayCurrencyInProject,
      projectBaseCurrency,
      amount,
    );
  }
  setDisplayCurrencySymbol(appConfig) {
    // if there is projectCurrencies, means it is in project level, else is company wide,
    // thus should show base currency symbol instead of display currency symbol
    const { projectCurrencies = [] } = this.state;
    if (projectCurrencies.length > 0) {
      return getDisplayCurrencySymbol();
    }
    return (appConfig && appConfig.data && appConfig.data.currency) ? appConfig.data.currency.symbol : '';
  }
  getValueName = (value, arr) => {
    const { t } = this.props;
    const valueName = arr.find(vn => vn.value === value);
    if (valueName && valueName.text) {
      return t(valueName.text, { ns: TRANSLATION_FILES.constants });
    }
    return '';
  }
  getBarStatusColor = (statusID) => {
    switch (statusID) {
      case 'reserved':
        return 'yellow';
      case 'booked':
        return 'red';
      case 'cancelled_developer':
        return 'brown';
      case 'cancelled_availability':
        return 'brown';
      case 'expired':
        return 'brown';
      case 'sold_spa_signed':
        return 'teal';
      default:
        return 'teal';
    }
  }
  getBarPercent = (data, statusCount) => {
    const total = Math.max.apply(Math, data.map((m) => {
      if (m.statusID !== 'total') {
        return m.count;
      }
      return 0;
    }));
    if (total) {
      return Math.floor((statusCount / total) * 100);
    }
    return 0;
  }
  checkData = (data) => {
    const newData = [];
    const arr = ['reserved', 'booked', 'cancelled_developer', 'sold_spa_signed', 'cancelled_availability', 'expired', 'total'];
    data.forEach((s) => {
      const newStatus = s;
      switch (newStatus.statusID) {
        case 'reserved': {
          newStatus.order = 1;
          newData.push(newStatus);
          break;
        }
        case 'booked': {
          newStatus.order = 2;
          newData.push(newStatus);
          break;
        }
        case 'cancelled_developer': {
          newStatus.order = 3;
          newData.push(newStatus);
          break;
        }
        case 'sold_spa_signed': {
          newStatus.order = 4;
          newData.push(newStatus);
          break;
        }
        case 'cancelled_availability': {
          newStatus.order = 5;
          newData.push(newStatus);
          break;
        }
        case 'expired': {
          newStatus.order = 6;
          newData.push(newStatus);
          break;
        }
        case 'total': {
          break; // remove total
        }
        default:
          newData.push(newStatus);
          break;
      }
    });
    arr.forEach((s, idx) => {
      const newStatus = {
        order: idx,
        statusID: s,
        count: 0,
        sumSPAValue: 0,
      };
      const found = data.find(f => f.statusID === s);
      if (!found) {
        newData.push(newStatus);
      }
    });
    const sortedData = newData.sort((a, b) => {
      if (a.order < b.order) {
        return -1;
      }
      if (a.order > b.order) {
        return 1;
      }
      return 0;
    });
    return sortedData;
  }
  async fetchReports(props = this.props) {
    await this.setLoading(true);
    const { store } = this.context;
    const { t } = this.props;
    const q = decodeSearch(props.location.search);
    const projectID = (props.projectID || q.projectID) || '';
    const query = Object.assign({}, q);
    if (projectID !== '') {
      query.projectID = projectID;
    }
    let res;
    try {
      res = await store.dispatch(fetchM(`${config.get('bookApiURL')}/reports/bookings/statuses/?${qs.stringify(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const reportData = await res.json();
      await this.setComponentState({
        reportData,
      });
    }
    await this.setLoading(false);
  }
  renderTable = () => {
    const { barChart, headerColor, appConfig, t } = this.props;
    const { projectCurrencies } = this.state;
    const data = this.checkData(this.state.reportData);
    if (data && data.length > 0) {
      return (
        <Table
          basic={barChart && 'very'}
          celled={!barChart}
          structured={!barChart}
          color={!barChart ? headerColor : null}
          compact
          unstackable
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('Booking Status')}</Table.HeaderCell>
              {barChart && <Table.HeaderCell />}
              <Table.HeaderCell textAlign={barChart ? 'left' : 'right'} className={barChart ? 'hide-mobile-six-hundred-px' : null}>{t('Count')}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{t('Selling Price Sum')}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{t('Nett Price Sum')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {
              data.map((statusReport, idx) => (
                <Table.Row
                  key={idx}
                  style={statusReport.statusID === 'total' ? { fontWeight: 'bold' } : {}}
                >
                  <Table.Cell>
                    {this.getValueName(statusReport.statusID, BOOKING_STATUSES)}
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    {
                      barChart ? (
                        <CountUp
                          duration={2.1}
                          end={this.state.loading ? 0 : statusReport.count}
                          start={0}
                          useEasing
                        />
                      ) : statusReport.count
                    }
                  </Table.Cell>
                  {
                    barChart &&
                    <Table.Cell textAlign="left" className="hide-mobile-six-hundred-px" width={3}>
                      <Progress
                        className={`progress-barchart ${statusReport.statusID}`}
                        percent={this.state.loading
                          ? 0
                          : this.getBarPercent(data, statusReport.count)
                        }
                      />
                    </Table.Cell>
                  }
                  <Table.Cell textAlign="right">
                    {
                      barChart ? (
                        <CountUp
                          decimals={2}
                          redraw
                          duration={2}
                          end={this.getConversionAmount(statusReport.sumSPAValue)}
                          prefix={this.setDisplayCurrencySymbol(appConfig)}
                          useEasing
                          useGrouping
                          separator=","
                        />
                      ) : (
                        <LoanAmount
                          projectCurrencies={projectCurrencies}
                          amount={statusReport.sumSPAValue}
                          format="0,000,000.00"
                          prefix={<CurrencySymbol />}
                        />
                      )
                    }
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    {
                      barChart ? (
                        <CountUp
                          decimals={2}
                          redraw
                          duration={2}
                          end={this.getConversionAmount(statusReport.sumNettValue)}
                          prefix={this.setDisplayCurrencySymbol(appConfig)}
                          useEasing
                          useGrouping
                          separator=","
                        />
                      ) : (
                        <LoanAmount
                          projectCurrencies={projectCurrencies}
                          amount={statusReport.sumNettValue}
                          format="0,000,000.00"
                          prefix={<CurrencySymbol />}
                        />
                      )
                    }
                  </Table.Cell>
                </Table.Row>
              ))
            }
          </Table.Body>
        </Table>
      );
    }
    return null;
  }
  render() {
    const { barChart, headerColor, t } = this.props;
    return (
      <Grid style={{ margin: '-1rem 0' }}>
        <Grid.Row style={barChart && { paddingTop: '0' }}>
          <Grid.Column>
            <Dimmer
              inverted style={{ zIndex: 20 }}
              active={this.state.loading}
            >
              <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
            </Dimmer>
            {
              barChart ? (
                <Segment color={headerColor}>
                  {this.renderTable()}
                </Segment>
              ) : this.renderTable()
            }
            {(this.props.auth.isHeadquarters || this.props.auth.isSuperAdmin) &&
              <OverallBookingReports
                location={this.props.location}
              />
            }
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default withTranslation(namespaces)(connect(BookingsByStatus.mapStateToProps)(BookingsByStatus));



// WEBPACK FOOTER //
// ./src/components/Reports/BookingsByStatus.jsx